import React, { useCallback, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";

import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";
import {
  useElementValue,
  useIsReadOnly,
} from "../DocumentView/DocumentContext";
import HiddenElementBanner from "./HiddenElementBanner";
import ArchivedElementBanner from "./ArchivedElementBanner";
import { type FileRef, useFormResultUpload, useResultFile } from "./useFormResultUpload";
import { FieldHeader, FieldHelpText } from "./FieldParts";
import { useImageLightbox } from "./ImageLightbox";

interface Props {
  element: DocumentElementType;
}

function isFileRef(v: unknown): v is FileRef {
  return (
    !!v &&
    typeof v === "object" &&
    typeof (v as { fileId?: unknown }).fileId === "number" &&
    typeof (v as { name?: unknown }).name === "string"
  );
}

const Signature: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
  };
  const label = data?.label || "Signature";
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = !!data?.required;

  const [rawValue, setValue] = useElementValue<FileRef | null>(element.id);
  const captured = isFileRef(rawValue) ? rawValue : null;
  const isReadOnly = useIsReadOnly();

  const sigCanvas = useRef<SignatureCanvas | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const { pending, pickFiles, clearPending } = useFormResultUpload();
  const [saving, setSaving] = useState(false);
  const inFlight = pending[0];

  const clearCanvas = useCallback(() => {
    sigCanvas.current?.clear();
    setHasInk(false);
  }, []);

  const handleSave = useCallback(async () => {
    const canvas = sigCanvas.current?.getCanvas();
    if (!canvas) return;
    setSaving(true);
    try {
      // The drawing canvas is transparent (the ruled-line guide behind it
      // must show through), so composite onto white before export — a
      // transparent PNG reads as a black box on dark surfaces (lightbox,
      // dark-mode results). Native captures on white already.
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = canvas.width;
      exportCanvas.height = canvas.height;
      const ctx = exportCanvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      const blob = await new Promise<Blob | null>((resolve) =>
        exportCanvas.toBlob((b) => resolve(b), "image/png"),
      );
      if (!blob) return;
      const file = new File([blob], `signature-${Date.now()}.png`, {
        type: "image/png",
      });
      const refs = await pickFiles([file]);
      if (refs.length > 0) {
        setValue(refs[0]);
        clearCanvas();
      }
    } finally {
      setSaving(false);
    }
  }, [pickFiles, setValue, clearCanvas]);

  const handleRemove = useCallback(() => {
    setValue(null);
    setHasInk(false);
  }, [setValue]);

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />

      {captured ? (
        <CapturedSignature
          fileRef={captured}
          onRemove={isReadOnly ? undefined : handleRemove}
        />
      ) : isReadOnly ? (
        <div className="flex h-[108px] items-center justify-center rounded-[12px] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white">
          <p className="text-[13px] italic text-[var(--forms-optional)]">
            No signature
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[12px] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white">
          <div
            className="relative h-[108px]"
            style={{
              background:
                "repeating-linear-gradient(0deg, transparent, transparent 26px, #f2f4f7 26px, #f2f4f7 27px)",
            }}
          >
            <SignatureCanvas
              ref={(c) => {
                sigCanvas.current = c;
              }}
              penColor="#1f2a3d"
              canvasProps={{
                className: "h-full w-full",
                width: 600,
                height: 108,
                style: { width: "100%", height: 108, touchAction: "none" },
              }}
              onEnd={() => setHasInk(true)}
            />
          </div>
          <div className="flex items-center justify-between border-t border-[var(--forms-page)] bg-[var(--forms-addon-bg)] px-[13px] py-[9px]">
            <span className="text-[12px] text-[var(--forms-muted-soft)]">
              {saving ? "Saving…" : hasInk ? "Ready to save" : "Sign above"}
            </span>
            <div className="flex items-center gap-[14px]">
              <button
                type="button"
                onClick={clearCanvas}
                disabled={!hasInk || saving}
                className="text-[12px] font-bold text-[var(--forms-muted-soft)] transition-colors hover:text-[var(--forms-ink)] disabled:opacity-40"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasInk || saving}
                className="text-[12px] font-bold text-[var(--forms-accent,var(--forms-accent-default))] transition-colors hover:underline disabled:opacity-40"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {inFlight && inFlight.state === "error" && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
          <p className="text-destructive">
            {inFlight.errorMessage ?? "Upload failed"}
          </p>
          <button
            type="button"
            onClick={() => clearPending(inFlight.uploadId)}
            className="mt-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      )}

      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

const CapturedSignature: React.FC<{
  fileRef: FileRef;
  onRemove?: () => void;
}> = ({ fileRef, onRemove }) => {
  const { file, loading } = useResultFile(fileRef.fileId);
  const { openImage, lightbox } = useImageLightbox();
  return (
    <div className="overflow-hidden rounded-[12px] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white">
      {file?.url ? (
        <button
          type="button"
          onClick={() =>
            openImage({
              fileId: fileRef.fileId,
              name: "Signature",
              previewUrl: file.thumbnailUrl ?? file.url,
              fullUrl: file.url,
            })
          }
          className="block w-full cursor-zoom-in"
          aria-label="View signature"
        >
          <img
            src={file.thumbnailUrl ?? file.url}
            alt="Signature"
            className="block max-h-48 w-full object-contain p-2"
          />
        </button>
      ) : (
        <div className="flex h-[108px] items-center justify-center">
          {loading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              className="h-4 w-4 animate-spin text-[var(--forms-optional)]"
            />
          ) : (
            <p className="text-[13px] italic text-[var(--forms-optional)]">
              Signature unavailable
            </p>
          )}
        </div>
      )}
      {onRemove && (
        <div className="flex items-center justify-between border-t border-[var(--forms-page)] bg-[var(--forms-addon-bg)] px-[13px] py-[9px]">
          <span className="text-[12px] text-[var(--forms-muted-soft)]">
            Signed
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="text-[12px] font-bold text-[var(--forms-accent,var(--forms-accent-default))] transition-colors hover:underline"
          >
            Clear
          </button>
        </div>
      )}
      {lightbox}
    </div>
  );
};

export default Signature;
